import type { KeyboardEvent, RefObject } from 'react'
import React, { createRef, useRef } from 'react'
import { TickIcon } from '@fourthwall/icons'
import { InputUI } from '../../ui'
import { Icon } from '../../common/Icon'
import * as S from './styled'
import { withFormikSupport } from '../../hocs/withFormikSupport'
import type { GridSelectProps } from './types'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'

const GridSelectComponent = ({
  name,
  value,
  options,
  error,
  disabled,
  numberOfColumns = 3,
  onChange,
}: GridSelectProps) => {
  const media = useMediaQueryContext()
  const inputsRef = useRef<RefObject<HTMLInputElement>[]>(options.map(() => createRef())).current

  const handleKeyDown = (event: KeyboardEvent<HTMLLabelElement>, index: number) => {
    if (event.key === 'Enter') {
      inputsRef[index].current?.click()
    }
  }

  const getEmptyOptions = (numberOfColumns: number, optionsLength: number): number[] => {
    const remainder = optionsLength % numberOfColumns

    return remainder ? [...Array(numberOfColumns - remainder).keys()] : []
  }

  return (
    <>
      <S.Container numberOfColumns={numberOfColumns} error={!!error}>
        {options.map((option, index) => {
          const id = `${name}.${option.value}`

          return (
            <S.Option
              key={option.value}
              $active={option.value === value}
              data-selected={option.value === value}
              tabIndex={0}
              htmlFor={id}
              $disabled={disabled}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <S.Input
                id={id}
                ref={inputsRef[index]}
                aria-hidden="true"
                type="radio"
                name={name}
                value={option.value}
                checked={option.value === value}
                disabled={disabled}
                onChange={onChange}
              />
              {option.value === value && (
                <S.ActiveTickContainer data-testid="GridSelect.Tick">
                  <Icon component={TickIcon} height={media.tablet ? 8 : 6} />
                </S.ActiveTickContainer>
              )}
              {option.label}
            </S.Option>
          )
        })}
        {getEmptyOptions(numberOfColumns, options.length).map((option) => {
          return <S.Option key={`empty${option}`} as="div" $empty data-testid="GridSelect.Empty" />
        })}
      </S.Container>
      {!!error && <InputUI.Error>{error}</InputUI.Error>}
    </>
  )
}

export const GridSelect = withFormikSupport(GridSelectComponent)
